import React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import { ProgressBar } from '@common/bw-ui/ui/progress-bar';

/**
 * The two-column signup shell from the redesign: a sticky intro column on the
 * left and the form card on the right, stacking on smaller screens. Both
 * columns grow with the viewport inside a centred 1440px container (the
 * "fluid columns" treatment), so large screens don't collect dead space on
 * the right.
 */
export const SignupSplit = ({ children }: { children: [React.ReactNode, React.ReactNode] }) => (
  <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[1440px] tw:flex-col tw:gap-10 tw:p-4 tw:sm:p-10 tw:lg:flex-row tw:lg:items-start tw:lg:gap-16 tw:xl:gap-24">
    {/* The sticky offset has to equal the column's own resting position, or it slides up by the
        difference before locking. 109px = the auth header (69px) + this container's sm:p-10 top
        padding (40px) - the two values to revisit if either of those changes. */}
    <div className="tw:w-full tw:lg:sticky tw:lg:top-[109px] tw:lg:min-w-[400px] tw:lg:flex-1 tw:lg:basis-0">
      <div className="tw:max-w-[560px]">{children[0]}</div>
    </div>
    <div className="tw:w-full tw:lg:min-w-[496px] tw:lg:max-w-[640px] tw:lg:flex-1 tw:lg:basis-0">
      {children[1]}
    </div>
  </div>
);

export interface ISignupIntroProps {
  headline: string;
  description?: React.ReactNode;
  steps?: number;
  currentStep?: number;
}

/**
 * Left-column intro: headline, supporting copy and the "Step N of M" progress
 * indicator.
 */
export const SignupIntro = (props: ISignupIntroProps) => (
  <div className="tw:flex tw:flex-col tw:gap-2">
    <h1 className="tw:m-0 tw:text-2xl tw:font-medium tw:text-foreground">{props.headline}</h1>
    {props.description && (
      <p className="tw:m-0 tw:text-base tw:font-normal tw:leading-7 tw:text-foreground">
        {props.description}
      </p>
    )}
    {props.steps && props.currentStep ? (
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:pt-2">
        <p className="tw:m-0 tw:text-sm tw:font-normal tw:text-foreground" data-testid="step-indicator">
          Step {props.currentStep} of {props.steps}
        </p>
        <ProgressBar steps={props.steps} currentStep={props.currentStep} />
      </div>
    ) : null}
  </div>
);

/** The bordered white card that holds each step's form fields. */
export const SignupCard = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={cn(
      'tw:flex tw:flex-col tw:gap-5 tw:rounded-[10px] tw:border tw:border-black/20 tw:bg-white tw:p-5',
      className,
    )}>
    {children}
  </div>
);

/** In-card section heading with the full-width rule underneath. */
export const SignupSectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
    <h2 className="tw:m-0 tw:text-base tw:font-medium tw:text-foreground">{children}</h2>
    <div className="tw:h-px tw:w-full tw:bg-black/20" aria-hidden="true" />
  </div>
);
